import { createUseStyles } from 'react-jss';

interface Props {
  icon: any;
  backgroundColor: any;
  backgroundColorHover: any;
  textColor: any;
  overlap: any;
};

const useStyles = createUseStyles((theme: any) => ({
  eventWrapper: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: (props: Props) => {
      if(props.overlap === 'full') return '100%';
      else if(props.overlap === 'left' || props.overlap === 'right') return 'calc(100% - 4px)';
      else return 'calc(100% - 8px)';
    },
    borderRadius: (props: Props) => {
      if(props.overlap === 'full') return '0px';
      else if(props.overlap === 'left') return '12px 0 0 12px';
      else if(props.overlap === 'right') return '0 12px 12px 0';
      else return '12px';
    },
    paddingLeft: (props: Props) => {
      if(props.overlap === 'left') return '4px';
      else return '';
    },
    paddingRight: (props: Props) => {
      if(props.overlap === 'right') return '4px';
      else return '';
    },
  },
  event: {
    backgroundColor: (props: Props) => props.backgroundColor,
    borderRadius: 'inherit',
    width: 'calc(100% - 16px)',
    padding: '0 8px',
    height: '30px',
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    position: 'relative',
    color: (props: Props) => props.textColor,
    transition: 'background-color 0.25s',
    cursor: 'pointer',
    '& > svg': {
      display: 'block',
      width: '16px',
      height: '16px',
      minWidth: '16px',
      minHeight: '16px',
    },
    '& > img': {
      display: 'block',
      width: '16px',
      height: '16px',
      minWidth: '16px',
      minHeight: '16px',
      filter: 'invert(100%)',
    },
    '&:hover': {
      backgroundColor: (props: Props) => props.backgroundColorHover,
    },
    '&.hover': {
      backgroundColor: (props: Props) => props.backgroundColorHover,
    },
  },
  name: {
    maxWidth: '100%',
    fontSize: '11px',
    lineHeight: '13px',
    textAlign: 'center',
    wordBreak: 'break-all',
    margin: 'auto 0',
    fontWeight: '600',
    [theme.breakpoints.down('sm')]: {
      fontSize: '9px',
    },
  },
  popover: {
    '& .MuiPopover-paper': {
      display: 'flex',
      flexDirection: 'column',
      width: '400px',
      boxShadow: theme.shadows[1],
      borderRadius: '12px',
      backgroundColor: theme.colors.white,
    },
  },
  popoverHeader: {
    display: 'block',
    width: '100%',
    height: '100px',
    position: 'relative',
    overflow: 'hidden',
    minHeight: '100px',
    backgroundColor: (props: Props) => props.backgroundColor,
    '&::after': {
      content: `''`,
      position: 'absolute',
      top: '-10px',
      left: '-10px',
      right: '-10px',
      bottom: '-10px',
      backgroundImage: (props: Props) => `url("${props.icon}")`,
      backgroundSize: '26px 26px',
      backgroundRepeat: 'space',
      filter: 'invert(100%)',
      zIndex: '0',
      opacity: '0.7',
    }
  },
  popoverTopButtons: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    display: 'flex',
    gap: '8px',
    zIndex: '1',
    padding: '2px 4px',
  },
  popoverBottomButtons: {
    position: 'absolute',
    bottom: '8px',
    right: '8px',
    display: 'flex',
    gap: '8px',
    zIndex: '1',
    backgroundColor: theme.colors.white,
    padding: '2px 4px',
    borderRadius: '12px',
  },
  popoverButton: {
    backgroundColor: 'rgba(0,0,0,0.5)',
    padding: '0',
    '& > svg': {
      width: '14px',
      height: '14px',
    },
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.3)',
    },
  },
  popoverDetails: {
    display: 'flex',
    flexDirection: 'column',
    width: 'calc(100% - 32px)',
    padding: '16px',
    gap: '8px',
    maxHeight: '500px',
    overflow: 'hidden auto',
  },
  eventName: {
    fontSize: '18px',
    fontWeight: '500',
    wordBreak: 'break-all',
  },
  popoverDetail: {
    display: 'flex',
    width: '100%',
  },
  popoverDetailLeft: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    flex: '0 0 32px',
    width: '32px',
    padding: '0 8px',
    '&.iconTop': {
      alignItems: 'flex-start',
      paddingTop: '2px',
    },
    '& > svg': {
      width: '20px',
      height: '20px',
    },
  },
  popoverDetailRight: {
    flex: '0 0 calc(100% - 48px)',
    width: 'calc(100% - 48px)',
  },
  popoverEventIcon: {
    width: '20px',
    height: '20px',
    borderRadius: '6px',
    backgroundColor: (props: Props) => props.backgroundColor,
  },
  eventDates: {
    display: 'flex',
    gap: '8px',
  },
  eventDay: {
    display: 'inline-flex',
  },
  eventTime: {
    display: 'inline-flex',
  },
  eventDayTime: {
    display: 'inline-flex',
  },
  eventDelimiter: {
    display: 'inline-flex',
  },
  classList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    width: '100%',
  },
  theClassWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    width: '100%',
  },
  theClass: {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    fontSize: '14px',
    fontWeight: '800',
    wordBreak: 'break-all',
    paddingBottom: '8px',
  },
  guestList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    width: '100%',
  },
  guest: {
    display: 'flex',
    width: '100%',
    gap: '8px',
    alignItems: 'center',
    color: theme.colors.primaryBlue[500],
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    wordBreak: 'break-all',
    '&.noLink': {
      color: theme.colors.black,
      fontWeight: '400',
      cursor: 'auto',
    },
  },
  guestChildPhoto: {
    position: 'relative',
    width: '20px',
    height: '20px',
    minWidth: '20px',
    minHeight: '20px',
    maxWidth: '20px',
    maxHeight: '20px',
    borderRadius: '100%',
  },
  guestUserPhoto: {
    position: 'relative',
    width: '20px',
    height: '20px',
    minWidth: '20px',
    minHeight: '20px',
    maxWidth: '20px',
    maxHeight: '20px',
    borderRadius: '6px',
  },
  description: {
    fontSize: '14px',
  },
  showToggleWrapper: {
    display: 'flex',
    width: '100%',
    justifyContent: 'center',
    marginTop: '0.5rem',
    backgroundColor: theme.colors.white,
  },
  showToggle: {
    color: theme.colors.primaryBlue[500],
    cursor: "pointer",
    fontWeight: 'bold',
    textTransform: 'uppercase',
    transition: 'color 0.25s',
    '&:hover': {
      color: theme.colors.primaryBlue[600],
    },
  },
  count: {
    display: 'block',
    paddingBottom: '8px',
  },
  shareCalendarButtons: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 0',
    '& > button': {
      height: '32px',
      width: 'fit-content',
      padding: '9px 12px',
      '& > span': {
        fontSize: '12px',
      },
      '& svg': {
        width: '24px',
        height: '24px',
      },
    },
  },
  externalCalendarButtons: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    '& > button': {
      position: 'relative',
      height: '32px',
      width: 'fit-content',
      padding: '9px 12px',
      borderRadius: '0 10px 10px 0',
      marginLeft: '28px',
      '& > span': {
        fontSize: '12px',
      },
      '& svg': {
        position: 'absolute',
        top: '50%',
        left: '-28px',
        transform: 'translateY(-50%)',
        width: '38px',
        height: '38px',
        zIndex: '1',
      },
    },
  },
  tag: {
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '10px',
    padding: '2px 8px',
    width: 'fit-content',
    borderRadius: '10px',
    backgroundColor: '#EEEEEE',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
}));

export default useStyles;